import * as React from "react";
import { useTranslation } from "react-i18next";
import { useContent, text } from "../hooks/useContent";
import { SideNav } from "./ui/SideNav";
import type { SideNavItem, SideNavTone } from "./ui/SideNav";
import { computeSectionProgress } from "./scene/utils";
import type { SceneThresholds } from "./scene/responsive";
import { useSceneScalars } from "../hooks/useSceneScalars";
import { useBreakpoint } from "../hooks/useBreakpoint";
import {
  homeScrollLayout,
  decksScrollLayout,
  radioScrollLayout,
  useScrollLayout,
} from "./scene/scrollLayout";
import { aboutStartOffset, mainNavState } from "./sideNavState";
import type { MainNavState } from "./sideNavState";
import { deriveDeckWindows, DEFAULT_DECKS_MEASUREMENT } from "./scene/parts/decksScrollWindows";
import { RADIO_FEATURE_COUNT } from "./RadioPageContent";

// Labels computed in the component via useTranslation("nav")

/**
 * Per-anchor target in logical t-space. We map t → scrollTop via tScale so
 * each click lands where the scene is fully entered, not partway through the
 * transition. Decks target = tMax (full entry); services target sits
 * just past the home→services threshold so the camera is firmly in services.
 */
function mainTTarget(th: SceneThresholds, tMax: number): Record<string, number> {
  return {
    services: th.homeServices + 0.5,
    decks: th.servicesDecks + 0.5,
    reviews: tMax,
  };
}

// Upper bound for the last subsection's progress range. scrollEl offset is
// normalised to [0, 1], so 1.0 is the end of the page.
const DECKS_SCROLL_END = 1.0;

type DecksThreshold = { id: string; t: number };

function activeDecksSection(thresholds: DecksThreshold[], offset: number): string {
  let active = thresholds[0].id;
  for (const { id, t } of thresholds) {
    if (offset >= t) active = id;
  }
  return active;
}

function decksProgress(thresholds: DecksThreshold[], offset: number, activeId: string): number {
  const idx = thresholds.findIndex((s) => s.id === activeId);
  if (idx < 0) return 0;
  const start = thresholds[idx].t;
  const end =
    idx + 1 < thresholds.length
      ? thresholds[idx + 1].t
      : DECKS_SCROLL_END;
  return computeSectionProgress(offset, start, end);
}

type Props = {
  activePage: string;
  scrollEl: HTMLElement | null;
  activeScene: string;
};

export function SideNavOverlay({ activePage, scrollEl, activeScene }: Props) {
  // services / guidanceEdition / mediumshipDeck have no CMS counterpart →
  // sourced from apps.translations. decks / reviews are CMS-driven (same
  // SiteText rows the Navbar uses) so admins edit them in one place.
  const { t } = useTranslation("nav");
  const { data: content } = useContent();
  const scalars = useSceneScalars();
  const breakpoint = useBreakpoint();
  const mainTargets = mainTTarget(scalars.thresholds, scalars.tMax);
  const layout = useScrollLayout(homeScrollLayout);
  const aboutStart = aboutStartOffset(layout);
  const [decksActiveId, setDecksActiveId] = React.useState("hero");
  const [radioActiveId, setRadioActiveId] = React.useState("overview");
  const [progress, setProgress] = React.useState(0);
  const [mainNav, setMainNav] = React.useState<MainNavState>({ activeId: "home", progress: 0 });

  // Decks subsection boundaries derived from the measured DOM (scroll-sync
  // rule: no hand-tuned offset literals). A section becomes active when its
  // panel starts fading in; the bundle takes over once the second deck panel
  // (guidance) has fully faded out.
  const decksLayout = useScrollLayout(decksScrollLayout);
  const decksThresholds = React.useMemo(() => {
    const w =
      (decksLayout && deriveDeckWindows(decksLayout)) ??
      deriveDeckWindows(DEFAULT_DECKS_MEASUREMENT)!;
    return [
      { id: "hero", t: 0.0 },
      { id: "mediumship-deck", t: w.text.d1In },
      { id: "guidance-edition", t: w.text.d2In },
      { id: "deck-bundle", t: w.text.d2OutEnd },
    ];
  }, [decksLayout]);

  const mainItems: SideNavItem[] = [
    { id: "services", label: t("services") },
    { id: "decks", label: text(content, "nav.decks") },
    { id: "reviews", label: text(content, "nav.reviews") },
    { id: "about", label: text(content, "nav.about") },
  ];

  const decksItems: SideNavItem[] = [
    { id: "mediumship-deck", label: t("mediumshipDeck") },
    { id: "guidance-edition", label: t("guidanceEdition") },
    { id: "deck-bundle", label: t("bundle") },
  ];

  // /spirit-box rail — a curated 4-landmark index (the page has 16 sections). Labels
  // are CMS-driven (same SiteText store as the Navbar); targets are scrollTop
  // px derived from the measured anchors via the drei px→scrollTop mapping
  // (anchorPx · pages/(pages−1), same as RadioPageContent's scrollToFeatures).
  // Specs + FAQ moved into Order-section modals, so the rail indexes the
  // in-flow landmarks instead: overview → how-to → who-it's-for → included →
  // order. Ascending scroll order (radioTargets sorts by measured top).
  const radioItems: SideNavItem[] = [
    { id: "overview", label: text(content, "radio.nav.overview") },
    { id: "howto", label: text(content, "radio.nav.howto") },
    { id: "whofor", label: text(content, "radio.nav.whofor") },
    { id: "included", label: text(content, "radio.nav.included") },
    { id: "order", label: text(content, "radio.nav.order") },
  ];
  const radioLayout = useScrollLayout(radioScrollLayout);
  const radioTargets = React.useMemo(() => {
    const m = radioLayout;
    if (!m || m.vh <= 0) return null;
    const pages = m.contentHeight / m.vh;
    if (!(pages > 1)) return null;
    const toTop = (px: number) => (px * pages) / (pages - 1);
    const anchorFor: Record<string, string> = {
      overview: "s1",
      howto: `s${RADIO_FEATURE_COUNT + 2}`,
      whofor: `s${RADIO_FEATURE_COUNT + 3}`,
      included: `s${RADIO_FEATURE_COUNT + 4}`,
      order: "order",
    };
    const out = radioItems
      .map((it) => ({ id: it.id, px: m.anchors[anchorFor[it.id]] }))
      .filter((x) => x.px != null)
      .map((x) => ({ id: x.id, top: toTop(x.px as number) }));
    return out.length ? out : null;
    // radioItems is rebuilt each render but only the ids/anchors matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radioLayout]);

  // Subscribe to scroll for both modes. We coalesce to a single rAF tick to
  // avoid recomputing on every wheel event.
  React.useEffect(() => {
    if (!scrollEl) return;
    let pending = false;

    const update = () => {
      pending = false;
      const max = scrollEl.scrollHeight - scrollEl.clientHeight;
      const offset = max > 0 ? scrollEl.scrollTop / max : 0;
      if (activePage === "decks") {
        const id = activeDecksSection(decksThresholds, offset);
        setDecksActiveId(id);
        setProgress(decksProgress(decksThresholds, offset, id));
      } else if (activePage === "radio") {
        if (radioTargets) {
          // A landmark goes active once its anchor is ~45% up the viewport.
          const cursor = scrollEl.scrollTop + scrollEl.clientHeight * 0.45;
          let id = radioTargets[0].id;
          for (const tgt of radioTargets) if (cursor >= tgt.top) id = tgt.id;
          const idx = radioTargets.findIndex((s) => s.id === id);
          const start = radioTargets[idx].top;
          const end = idx + 1 < radioTargets.length ? radioTargets[idx + 1].top : max;
          setRadioActiveId(id);
          setProgress(computeSectionProgress(scrollEl.scrollTop, start, end));
        }
      } else {
        setMainNav(
          mainNavState({
            offset,
            activeScene,
            thresholds: scalars.thresholds,
            tScale: scalars.tScale,
            tMax: scalars.tMax,
            aboutStart,
          }),
        );
      }
    };

    const onScroll = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(update);
    };

    update();
    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    return () => scrollEl.removeEventListener("scroll", onScroll);
  }, [activePage, scrollEl, activeScene, scalars.tScale, scalars.tMax, scalars.thresholds, aboutStart, decksThresholds, radioTargets]);

  const handleSelect = React.useCallback(
    (id: string) => {
      if (!scrollEl) return;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (activePage === "decks") {
        const t = decksThresholds.find((s) => s.id === id)?.t ?? 0;
        const max = scrollEl.scrollHeight - scrollEl.clientHeight;
        const target = Math.max(0, Math.min(max, t * max));
        scrollEl.scrollTo({ top: target, behavior: reduce ? "auto" : "smooth" });
      } else if (activePage === "radio") {
        const tgt = radioTargets?.find((s) => s.id === id);
        if (!tgt) return;
        const max = scrollEl.scrollHeight - scrollEl.clientHeight;
        scrollEl.scrollTo({
          top: Math.max(0, Math.min(max, tgt.top)),
          behavior: reduce ? "auto" : "smooth",
        });
      } else if (id === "about") {
        if (aboutStart == null) return;
        const max = scrollEl.scrollHeight - scrollEl.clientHeight;
        // +1px: цель клика совпадает с порогом активации (offset >= aboutStart),
        // а браузер округляет scrollTop вниз — без перелёта посадка остаётся
        // на epsilon ниже порога и пункт не подсвечивается.
        const top = Math.min(max, aboutStart * max + 1);
        scrollEl.scrollTo({ top, behavior: reduce ? "auto" : "smooth" });
      } else {
        const t = mainTargets[id] ?? 0;
        const max = scrollEl.scrollHeight - scrollEl.clientHeight;
        const top = Math.max(0, Math.min(max, (t / scalars.tScale) * max));
        scrollEl.scrollTo({ top, behavior: reduce ? "auto" : "smooth" });
      }
    },
    [scrollEl, activePage, mainTargets, scalars.tScale, aboutStart, decksThresholds, radioTargets],
  );

  if (!scrollEl) return null;

  // The floating section index is desktop/tablet only; on phones the burger
  // menu is the navigation surface (the side rail would crowd the viewport).
  if (breakpoint === "mobile") return null;

  // Inverse (white) palette only while the camera is in the dark
  // services-fog scene. Decks (#9ac0de) and Reviews (#fff) keep the
  // default dark-blue palette. The /decks route uses its own DECKS_ITEMS
  // overlay and is never inverse. /spirit-box is nocturnal on desktop (inverse) but
  // keeps the light layout on tablet (primary).
  const tone: SideNavTone =
    activePage === "radio"
      ? breakpoint === "desktop"
        ? "inverse"
        : "primary"
      : activePage !== "decks" && activeScene === "services"
        ? "inverse"
        : "primary";

  if (activePage === "radio") {
    if (!radioTargets) return null;
    return (
      <SideNav
        items={radioItems}
        activeId={radioActiveId}
        activeProgress={progress}
        onSelect={handleSelect}
        tone={tone}
      />
    );
  }

  if (activePage === "decks") {
    if (decksActiveId === "hero") return null;
    return (
      <SideNav
        items={decksItems}
        activeId={decksActiveId}
        activeProgress={progress}
        onSelect={handleSelect}
        tone={tone}
      />
    );
  }

  if (activeScene === "home") return null;

  return (
    <SideNav
      items={mainItems}
      activeId={mainNav.activeId}
      activeProgress={mainNav.progress}
      onSelect={handleSelect}
      tone={tone}
    />
  );
}
